import { service } from '@ember/service';
import type { AdapterPayload } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type Store from '@ember-data/store';
import type { ModelSchema } from '@ember-data/store/types';
import ApplicationAdapter from 'lumen/adapters/application';
import config from 'lumen/config/environment';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { ACCESS_RIGHTS } from 'lumen/utils/constants/users-constants';
import { Promise as RsvpPromise } from 'rsvp';

interface SubscriptionUserSeed {
  access_right: string;
  email: string;
  first_name: string;
  has_bi_benchmarking_access: boolean;
  has_rate_insight_access: boolean;
  id: number;
  is_locked: boolean;
  last_active: string | null;
  last_name: string;
}

const SEED_USERS: SubscriptionUserSeed[] = [
  {
    id: 1,
    first_name: 'Alex',
    last_name: 'Johnson',
    email: 'alex.johnson@hotel.com',
    access_right: ACCESS_RIGHTS.ADMIN,
    has_rate_insight_access: true,
    has_bi_benchmarking_access: true,
    is_locked: false,
    last_active: '2026-02-16T10:30:00Z',
  },
  {
    id: 2,
    first_name: 'Iva',
    last_name: 'Ryan',
    email: 'iva.ryan@hotel.com',
    access_right: ACCESS_RIGHTS.READ,
    has_rate_insight_access: true,
    has_bi_benchmarking_access: false,
    is_locked: false,
    last_active: '2026-01-29T14:15:00Z',
  },
  {
    id: 3,
    first_name: 'Charlie',
    last_name: 'Brown',
    email: 'charlie.brown@hotel.com',
    access_right: ACCESS_RIGHTS.WRITE,
    has_rate_insight_access: false,
    has_bi_benchmarking_access: true,
    is_locked: true,
    last_active: '2026-03-10T08:45:00Z',
  },
  {
    id: 4,
    first_name: 'Diana',
    last_name: 'Prince',
    email: 'diana.prince@hotel.com',
    access_right: ACCESS_RIGHTS.ADMIN,
    has_rate_insight_access: true,
    has_bi_benchmarking_access: true,
    is_locked: false,
    last_active: null,
  },
  {
    id: 5,
    first_name: 'Eva',
    last_name: 'Martinez',
    email: 'eva.martinez@hotel.com',
    access_right: ACCESS_RIGHTS.WRITE,
    has_rate_insight_access: true,
    has_bi_benchmarking_access: true,
    is_locked: false,
    last_active: '2026-04-01T16:20:00Z',
  },
  {
    id: 6,
    first_name: 'Frank',
    last_name: 'Lee',
    email: 'frank.lee@hotel.com',
    access_right: ACCESS_RIGHTS.READ,
    has_rate_insight_access: false,
    has_bi_benchmarking_access: false,
    is_locked: false,
    last_active: '2026-04-22T09:00:00Z',
  },
  {
    id: 7,
    first_name: 'Grace',
    last_name: 'Kim',
    email: 'grace.kim@hotel.com',
    access_right: ACCESS_RIGHTS.ADMIN,
    has_rate_insight_access: true,
    has_bi_benchmarking_access: true,
    is_locked: true,
    last_active: '2026-04-15T11:30:00Z',
  },
  {
    id: 8,
    first_name: 'Hank',
    last_name: 'Wilson',
    email: 'hank.wilson@hotel.com',
    access_right: ACCESS_RIGHTS.WRITE,
    has_rate_insight_access: true,
    has_bi_benchmarking_access: false,
    is_locked: false,
    last_active: '2026-03-28T13:45:00Z',
  },
];

let mockUsers = [...SEED_USERS];

const SORT_FIELD_MAP: Record<string, keyof SubscriptionUserSeed> = {
  name: 'first_name',
  role: 'access_right',
};

export default class SubscriptionUserAdapter extends ApplicationAdapter {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  private buildMockQueryResponse(
    query: Record<string, unknown>,
  ): Record<string, unknown> {
    const page = Number(query['page'] ?? 1);
    const pageSize = Number(query['page_size'] ?? 100);
    const sortBy = String((query['sort_by'] as string | undefined) ?? 'name');
    const sortDirection = String(
      (query['sort_direction'] as string | undefined) ?? 'asc',
    );
    const search = query['search'] as string | undefined;
    const role = query['role'] as string | undefined;
    const product = query['product'] as string | undefined;

    let filtered = [...mockUsers];

    if (search) {
      const terms = search.split(',').map((t) => t.trim().toLowerCase());
      filtered = filtered.filter((u) =>
        terms.some(
          (term) =>
            u.first_name.toLowerCase().includes(term) ||
            u.last_name.toLowerCase().includes(term) ||
            u.email.toLowerCase().includes(term),
        ),
      );
    }

    if (role) {
      filtered = filtered.filter((u) => u.access_right === role);
    }

    if (product === 'rate_insight') {
      filtered = filtered.filter((u) => u.has_rate_insight_access);
    } else if (product === 'bi_benchmarking') {
      filtered = filtered.filter((u) => u.has_bi_benchmarking_access);
    }

    const sortField = SORT_FIELD_MAP[sortBy] ?? 'first_name';
    const sortedUsers = filtered.sort((a, b) => {
      const aVal = String(a[sortField] ?? '');
      const bVal = String(b[sortField] ?? '');
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    const start = (page - 1) * pageSize;
    const paginatedUsers = sortedUsers.slice(start, start + pageSize);

    return {
      users: paginatedUsers,
      meta: {
        count: sortedUsers.length,
        page,
        page_size: pageSize,
        page_count: Math.ceil(sortedUsers.length / pageSize),
      },
    };
  }

  // @ts-expect-error: Property 'buildURL' in type 'SubscriptionUserAdapter' is not assignable to the same property in base type 'ApplicationAdapter'.
  buildURL(
    modelName: string,
    id: string,
    snapshot: unknown,
    requestType: string,
  ): string {
    if (requestType === 'deleteRecord') {
      const subscriptionId = this.hotelAndMarketService.hotel.id;
      return `${this.host}/${APIGATEWAY}/subscriptions/${subscriptionId}/users/${id}/`;
    }

    // @ts-expect-error: Expected 3 arguments, but got 4.
    return super.buildURL(modelName, id, snapshot, requestType);
  }

  deleteRecord(
    store: Store,
    type: ModelSchema,
    snapshot: Snapshot,
  ): Promise<AdapterPayload> {
    if (!config.isStagingOrDevelopment) {
      return super.deleteRecord(store, type, snapshot);
    }

    const userId = Number(snapshot.id);
    const adminCount = mockUsers.filter(
      (u) => u.access_right === ACCESS_RIGHTS.ADMIN && !u.is_locked,
    ).length;
    const userToDelete = mockUsers.find((u) => u.id === userId);

    if (
      userToDelete?.access_right === 'ADMIN' &&
      !userToDelete.is_locked &&
      adminCount <= 1
    ) {
      return Promise.reject(new Error('Cannot remove the last admin.'));
    }

    mockUsers = mockUsers.filter((u) => u.id !== userId);
    return Promise.resolve({} as AdapterPayload);
  }

  query(
    store: Store,
    type: ModelSchema,
    query: Record<string, unknown>,
  ): RsvpPromise<unknown> {
    if (!config.isStagingOrDevelopment) {
      return super.query(store, type, query);
    }

    return RsvpPromise.resolve(this.buildMockQueryResponse(query));
  }
}
